<script>
  import { onMount, afterUpdate } from "svelte";
  import { getLanguageInfo, SupportedLanguages } from "../common/localization";
  import coingecko from "../common/coingecko";
  import { Chart } from "chart.js/auto";
  import 'chartjs-adapter-date-fns';
  import MetadataGridItem from "./MetadataGridItem.svelte";
  import SummaryGridItem from "../lib/SummaryGridItem.svelte";
  import annotationPlugin from 'chartjs-plugin-annotation';
  import ChartDropdownMenu from "./ChartDropdownMenu.svelte";

  Chart.register(annotationPlugin);

  export let language = SupportedLanguages.English;
  export let chart = null;
  export let labels = [];
  export let data = [];
  export let metadata = {
      allTimeMaxIndex: -1,
      allTimeMinIndex: -1,
      allTimeMax: 0,
      allTimeMin: 0,
      currentDominance: 0,
      summaryItems: [],
      bitcoinMarketCap: "0",
      cryptoMarketCap: "0",
      weekRange: "",
      monthRange: "",
      yearRange: ""
    }

  const languageInfo = getLanguageInfo(language);

  let canvasRef = null;
  const canvasId = "bitcoin-dominance-chart";

  const getDifference = daysAgo => data.length > 0 ? data[data.length - daysAgo - 1] : null;

  const getYearIndex = (yearsAgo) => {
    if (data.length <= 0 || labels.length <= 0)
      return null;

    const currentYear = new Date(labels[labels.length - 1]).getFullYear();
    const d = new Date(labels[labels.length - 1]);
    d.setFullYear(currentYear - yearsAgo)
    d.setMonth(0);
    d.setDate(1);

    return labels.findIndex(l => {
      const labelDate = new Date(l);
      return labelDate.getFullYear() == d.getFullYear() && labelDate.getDate() == d.getDate();
    })
  }

  const asPercent = (val, includeSymbol = true) => Intl.NumberFormat(languageInfo.locale, { style: "decimal", maximumFractionDigits: 2 }).format(val) + (includeSymbol ? "%" : "");

  const asBigCurrency = val => {
    const trillion = 1_000_000_000_000;
    return val < trillion ?
      Intl.NumberFormat(languageInfo.locale, { style: 'currency', currency: languageInfo.currency, maximumFractionDigits: 2 }).format(val) :
      Intl.NumberFormat(languageInfo.locale, { style: 'currency', currency: languageInfo.currency, maximumFractionDigits: 2 }).format(val / trillion) + "T";
  }

  onMount(() => {
    fetchChartData();
    chart = new Chart(canvasRef, {
      type: "line",
      data: {
        labels: labels,
        datasets: [{
          label: "",
          fill: true,
          borderColor: ({ chart: { ctx, chartArea } }) => {
            if (!chartArea)
              return;

            const gradient = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);
            gradient.addColorStop(0, "#F2D22C");
            gradient.addColorStop(1, "#82D286");

            return gradient;
          },
          backgroundColor: ({ chart: { ctx, chartArea } }) => {
            if (!chartArea)
              return;

            const gradient = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);
            gradient.addColorStop(0, "rgba(131, 211, 134, 0)");
            gradient.addColorStop(.5, "rgba(230, 211, 55, 0.2)")
            gradient.addColorStop(1, "rgba(131, 211, 134, 1)");

            return gradient;
          },
          pointRadius: 0,
          pointHitRadius: 5,
          borderWidth: 1,
          data: []
        }]
      },
      options: {
        animation: false,
        responsive: true,
        maintainAspectRatio: false,
        normalized: true,
        plugins: {
          legend: {
            display: false
          },
          title: {
            display: false,
          },
          tooltip: {
            intersect: false,
            backgroundColor: "#001A38",
            titleFont: {
              size: 14,
              family: "Inter",
              weight: 500
            },
            bodyFont: {
              size: 14,
              family: "Inter",
              weight: 400,
            },
            cornerRadius: {
              topRight: 6,
              topLeft: 6,
              bottomLeft: 6,
              bottomRight: 6
            },
            boxPadding: 4,
            boxHeight: 12,
            boxWidth: 12,
            padding: 16,
            displayColors: false,
            callbacks: {
              labelPointStyle: _ => ({ pointStyle: "circle", rotation: 0 }),
              title: context => new Date(context[0].label.split(' ').slice(0, -1).join(" ")).toLocaleDateString(languageInfo.locale, {
                  day: "numeric",
                  month: "long",
                  year: "numeric"
              }),
              label: context => asPercent(Number.parseFloat("" + context.raw)),
            }
          },
          annotation: {
            // @ts-ignore
            annotations: getAnnotations()
          }
        },
        scales: {
          x: {
            type: "time",
            ticks: {
              autoSkip: true,
              maxRotation: 0,
              autoSkipPadding: 10,
              color: 'rgba(128, 154, 163, 1)',
            },
            grid: { 
              display: false
            }
          },
          y: {
            type: "linear",
            grace: "20%",
            ticks: {
              color: 'rgba(128, 154, 163, 1)',
              callback: val => {
                const numberVal = Number.parseFloat("" + val);
                if (numberVal <= 20 || numberVal > 100) {
                  return;
                }
                return val + "%";
              }
            }
          }
        }
      }
    })
  });

  const getAnnotations = () => {
    if (window.innerWidth <= 640) {
      return {};
    }
    return {
      maxLine: {
        type: "label",
        adjustScaleRange: false,
        backgroundColor: "#001A38",
        content: `High: ${asPercent(metadata.allTimeMax)}`,
        xValue: labels[metadata.allTimeMaxIndex + 200 ?? -1],
        yValue: 100,
        borderRadius: {
          topRight: 6,
          topLeft: 6,
          bottomLeft: 6,
          bottomRight: 6
        },
        color: "white"
      },
      minLine: {
        type: "label",
        adjustScaleRange: false,
        backgroundColor: "#001A38",
        content: `Low: ${asPercent(metadata.allTimeMin)}`,
        xValue: labels[metadata.allTimeMinIndex ?? -1],
        yValue: 25,
        borderRadius: {
          topRight: 6,
          topLeft: 6,
          bottomLeft: 6,
          bottomRight: 6
        },
        color: "white"
      }
    }
  }

  afterUpdate(() => {
    if (!chart) return;

    chart.data.labels = labels;
    chart.data.datasets[0].data = data;
    chart.options.plugins.annotation.annotations = getAnnotations();
    chart.update();
  });

  const findMaxAndMin = (arr, { startIndex = 0, endIndex = null } = { startIndex: 0, endIndex: null }) => {
    let maxIndex = -1;
    let minIndex = -1;
    let max = Number.MIN_VALUE;
    let min = Number.MAX_VALUE;
    for (let i = startIndex; i < (endIndex ?? arr.length - 1); i++) {
      var val = arr[i];
      if (val > max) {
        max = val;
        maxIndex = i;
      } else if (val < min) {
        min = val;
        minIndex = i;
      }
    }

    return { max, maxIndex, min, minIndex };
  }

  const fetchChartData = async function() {
    const [ marketCapChartResult, bitcoinPriceChartResult ] = await Promise.all([
      coingecko.getCryptoMarketCapChartData(languageInfo.currency),
      coingecko.getPriceChartData("bitcoin", languageInfo.currency)
    ]);

    if (marketCapChartResult.error || bitcoinPriceChartResult.error)
      return;

    const bitcoinPriceTimestamps = bitcoinPriceChartResult.prices.map(p => p[0]);

    const matches = [];
    for (let i = 0; i < marketCapChartResult.marketCaps.length; i++) {
      const marketCapTimestamp = marketCapChartResult.marketCaps[i][0];

      for (let j = 0; j < bitcoinPriceTimestamps.length; j++) {
        const bitcoinPriceTimestamp = bitcoinPriceTimestamps[j];

        if (bitcoinPriceTimestamp == marketCapTimestamp) {
          matches.push({ marketCapIndex: i, bitcoinPriceIndex: j });
          break;
        }
        // data is ordered, so if the current bitcoin price timestamp exceeds the marketcap timestamp, we will never find a match.
        if (bitcoinPriceTimestamp > marketCapTimestamp) {
          break;
        }
      }
    }

    data = matches.map(({ marketCapIndex, bitcoinPriceIndex }) => {
      return bitcoinPriceChartResult.marketCaps[bitcoinPriceIndex][1] / marketCapChartResult.marketCaps[marketCapIndex][1] * 100
    });

    labels = matches.map(({ marketCapIndex}) => marketCapChartResult.marketCaps[marketCapIndex][0]);

    const { max: allTimeMax, maxIndex, min: allTimeMin, minIndex } = findMaxAndMin(data);
    const { max: weeklyMax, min: weeklyMin } = findMaxAndMin(data, { startIndex: data.length - 8, endIndex: data.length - 1 });
    const { max: monthlyMax, min: monthlyMin } = findMaxAndMin(data, { startIndex: data.length - 31, endIndex: data.length - 1 });
    const { max: yearlyMax, min: yearlyMin } = findMaxAndMin(data, { startIndex: data.length - 366, endIndex: data.length - 1 });

    const currentDominance = data.length > 0 ? data[data.length - 1] : null
    metadata = {
      allTimeMaxIndex: maxIndex,
      allTimeMinIndex: minIndex,
      allTimeMax,
      allTimeMin,
      currentDominance,
      summaryItems: [
        { label: "1 day", diff: currentDominance - getDifference(1) },
        { label: "1 week", diff: currentDominance - getDifference(7) },
        { label: "1 month", diff: currentDominance - getDifference(30) },
        { label: "6 months", diff: currentDominance - getDifference(180) },
        { label: "Year to date", diff: currentDominance - getDifference(365) },
        { label: "1 year", diff: currentDominance - getDifference(data.length - getYearIndex(1)) },
        { label: "5 years", diff: currentDominance - getDifference(data.length - getYearIndex(5)) },
        { label: "All time", diff: currentDominance - getDifference(data.length - 1) }
      ],
      bitcoinMarketCap: bitcoinPriceChartResult.marketCaps[bitcoinPriceChartResult.marketCaps.length - 1][1],
      cryptoMarketCap: marketCapChartResult.marketCaps[marketCapChartResult.marketCaps.length - 1][1],
      weekRange: `${asPercent(weeklyMin, false)} - ${asPercent(weeklyMax)}`,
      monthRange: `${asPercent(monthlyMin, false)} - ${asPercent(monthlyMax)}`,
      yearRange: `${asPercent(yearlyMin, false)} - ${asPercent(yearlyMax)}`
    }
  }

  const options = [
    { name: "Weekly", value: "weekly" },
    { name: "Monthly", value: "monthly" },
    { name: "Yearly", value: "yearly" }
  ]

  $: selectValue = options[0].value;

  $: selectedRange = selectValue == "weekly" ?
    metadata.weekRange : selectValue == "monthly" ?
    metadata.monthRange : metadata.yearRange;
</script>

<div class="w-full font-sans py-6">
  <div class="mx-0 md:mx-12">
    <div class="flex items-center justify-between mb-6">
      <div class="flex items-center gap-x-2 text-base md:text-xl leading-8 font-medium text-neutral-1100">
        <img class="h-8 w-8 rounded" src="https://assets.coingecko.com/coins/images/1/large/bitcoin.png?1547033579" alt="" />
        <div>
          BTC Dominance Chart
        </div>
        {#if metadata.currentDominance != null}
          <div>
            {asPercent(metadata.currentDominance)}
          </div>
        {/if}
      </div>
      <div class="hidden md:block">
        <ChartDropdownMenu canvasId={canvasId} />
      </div>
    </div>
    <div class="md:hidden flex justify-end">
      <ChartDropdownMenu canvasId={canvasId} />
    </div>
    <div class="h-auto md:h-[254px] w-full">
      <canvas id="{canvasId}" bind:this={canvasRef}></canvas>
    </div>

    <div class="overflow-x-scroll flex items-center gap-3 mt-14">
      {#each metadata.summaryItems ?? [] as item}
        <MetadataGridItem label={item.label} value={item.diff} locale={languageInfo.locale} />
      {/each}
    </div>
  </div>

  <div class="flex flex-col items-center gap-y-8 mt-24">
    <div class="text-3xl font-medium text-neutral-1200">
      Key Market Stats
    </div>

    <div class="flex flex-col md:flex-row items-center justify-between gap-x-3 gap-y-3">
      <SummaryGridItem title="Bitcoin Market Cap" value={asBigCurrency(metadata.bitcoinMarketCap)} />
      <SummaryGridItem title="Total Crypto Market Cap" value={asBigCurrency(metadata.cryptoMarketCap)} />
      <SummaryGridItem title="Bitcoin Dominance Range" value={selectedRange} bind:selectValue={selectValue} selectOptions={options} />
    </div>
  </div>
</div>
